import type {PerkUsagePreviewComponentProps} from '@cohort/merchants/apps';
import PreviewButton from '@cohort/merchants/components/campaigns/preview/PreviewButton';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {CohortPromoCodePerkIntegrationStruct} from '@cohort/shared/apps/cohort/perks/promoCode';
import {DEFAULT_BACKGROUND_COLOR} from '@cohort/shared/schema/common';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {Check, Copy} from '@phosphor-icons/react';
import {Fragment, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

export const CohortPromoCodePerkIntegrationsUsagePreviewComponent: React.FC<
  PerkUsagePreviewComponentProps<CohortPromoCodePerkIntegrationStruct>
> = ({config, isMobile}) => {
  const [codeCopied, setCodeCopied] = useState(false);
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promo-code.usagePreviewComponent',
  });

  const computedCode: string | undefined = useMemo(() => {
    const defaultCode = t('defaultCode', {merchantName: merchant.name.toUpperCase()});

    return match(config)
      .with({type: 'unique'}, cfg => cfg.available?.[0] ?? defaultCode)
      .with({type: 'universal'}, cfg => cfg.code ?? defaultCode)
      .otherwise(() => defaultCode);
  }, [config, t, merchant.name]);

  const handleCopyCodeClicked = (): void => {
    setCodeCopied(true);
  };

  return (
    <Fragment>
      <div className="flex w-full flex-col space-y-4 rounded-[--xps-card-border-radius] bg-[--xps-card-background-color] p-4 text-center">
        <div className="flex flex-col gap-2">
          <p className="text-lg font-bold">{t('promoCodeTitle')}</p>
          <h2 className="text-3xl font-bold">{computedCode}</h2>
        </div>

        <PreviewButton
          size="small"
          variant="secondary"
          className={cn(isMobile && 'w-full')}
          backgroundColorCode={merchant.backgroundColorCode ?? DEFAULT_BACKGROUND_COLOR}
          onClick={handleCopyCodeClicked}
          text={
            codeCopied ? (
              <div className="flex items-center gap-x-1">
                <Check size={16} className="text-[--xps-p-color]" />
                {t('copied')}
              </div>
            ) : (
              <div className="flex items-center gap-x-1">
                <Copy size={16} className="text-[--xps-p-color]" />
                {t('copy')}
              </div>
            )
          }
        />
      </div>
    </Fragment>
  );
};
